import React from "react"
import { Post, Tag, CurrentUser } from "@fider/models"
import { ShowTag, Markdown, Icon, ResponseLozenge, EmptyState, VoteCounter } from "@fider/components"
import { i18n } from "@lingui/core"
import IconChatAlt2 from "@fider/assets/images/heroicons-chat-alt-2.svg"
import { HStack, VStack } from "@fider/components/layout"
import { useFider } from "@fider/hooks"
import { markdown } from "@fider/services"
import { isNewSincePreviousVisit } from "@fider/services/lastVisit"
import { Trans } from "@lingui/react/macro"

interface ListPostsProps {
  posts?: Post[]
  tags: Tag[]
  emptyText: string
  minimalView?: boolean
  onPostClick?: (postNumber: number, slug: string) => void
}

const ListPostItem = (props: { post: Post; user?: CurrentUser; tags: Tag[]; onPostClick?: (postNumber: number, slug: string) => void }) => {
  const fider = useFider()
  const isModerationEnabled = fider.session.tenant.isModerationEnabled
  const isPending = isModerationEnabled && !props.post.isApproved
  const isBug = props.post.tags.some((tag) => tag.toLowerCase() === "bug")
  const isNew = isNewSincePreviousVisit(props.post.createdAt)

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (props.onPostClick) {
      e.preventDefault()
      props.onPostClick(props.post.number, props.post.slug)
    }
  }

  const handleVoteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <a href={`/posts/${props.post.number}/${props.post.slug}`} className="c-posts-container__post-link" onClick={handleClick}>
      <HStack className="c-posts-container__post w-full" spacing={4} align="start">
        <div className="c-posts-container__post-vote flex-shrink-0" onClick={handleVoteClick}>
          <VoteCounter post={props.post} />
        </div>
        <VStack className="w-full" spacing={4}>
          <HStack justify="between" align="start">
            <HStack spacing={2} align="start" className="w-full">
              <h3 className="c-posts-container__post-title text-break">
                {isNew && (
                  <span className="c-posts-container__new">
                    <Trans id="home.post.new">New</Trans>
                  </span>
                )}
                {isBug && <span title="Bug">🐛 </span>}
                {props.post.title}
              </h3>
              {isPending && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded flex-shrink-0">
                  <Trans id="post.pending">pending</Trans>
                </span>
              )}
            </HStack>
            {props.post.commentsCount > 0 && (
              <HStack spacing={1} className="c-posts-container__post-comments flex-shrink-0">
                <span>{props.post.commentsCount}</span>
                <Icon sprite={IconChatAlt2} className="h-5 w-5" />
              </HStack>
            )}
          </HStack>
          <Markdown className="c-posts-container__postdescription" maxLength={300} text={markdown.postSummary(props.post.description)} style="plainText" />
          {props.tags.length >= 1 && (
            <HStack spacing={0} className="gap-2 flex-wrap">
              {props.tags.map((tag) => (
                <ShowTag key={tag.id} tag={tag} />
              ))}
            </HStack>
          )}
          {props.post.status !== "open" && (
            <div>
              <ResponseLozenge status={props.post.status} response={props.post.response} size={"small"} />
            </div>
          )}
        </VStack>
      </HStack>
    </a>
  )
}

const MinimalListPostItem = (props: { post: Post; tags: Tag[]; onPostClick?: (postNumber: number, slug: string) => void }) => {
  const fider = useFider()
  const isModerationEnabled = fider.session.tenant.isModerationEnabled
  const isPending = isModerationEnabled && !props.post.isApproved
  const isBug = props.post.tags.some((tag) => tag.toLowerCase() === "bug")

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (props.onPostClick) {
      e.preventDefault()
      props.onPostClick(props.post.number, props.post.slug)
    }
  }

  return (
    <HStack spacing={4} align="start" className="c-posts-container__post-minimal">
      <HStack className="w-full" justify="between" align="start">
        <HStack spacing={2} align="start" justify="between" className="w-full">
          <a className="text-link" href={`/posts/${props.post.number}/${props.post.slug}`} onClick={handleClick}>
            {isBug && <span title="Bug">🐛 </span>}
            {props.post.title}
          </a>
          {isPending && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">pending</span>}
        </HStack>
        {props.post.status !== "open" ? (
          <div>
            <ResponseLozenge status={props.post.status} response={props.post.response} size={"micro"} />
          </div>
        ) : (
          <span className="text-gray-700 text-sm">{props.post.votesCount >= 0 ? `+${props.post.votesCount}` : props.post.votesCount}</span>
        )}
      </HStack>
    </HStack>
  )
}

export const ListPosts = (props: ListPostsProps) => {
  const { minimalView = false } = props

  if (!props.posts) {
    return null
  }

  if (props.posts.length === 0) {
    return <EmptyState illustration="search" title={i18n._({ id: "home.listposts.empty.title", message: "No results found" })} description={props.emptyText} />
  }

  return (
    <>
      {minimalView ? (
        <VStack spacing={2}>
          {props.posts.map((post) => (
            <MinimalListPostItem
              key={post.id}
              post={post}
              tags={props.tags.filter((tag) => post.tags.indexOf(tag.slug) >= 0)}
              onPostClick={props.onPostClick}
            />
          ))}
        </VStack>
      ) : (
        <>
          {props.posts.map((post) => (
            <ListPostItem key={post.id} post={post} tags={props.tags.filter((tag) => post.tags.indexOf(tag.slug) >= 0)} onPostClick={props.onPostClick} />
          ))}
        </>
      )}
    </>
  )
}
